/**
 * Cliente del endpoint /api/v1/artifacts.
 * Backend: app/api/v1/artifacts.py.
 *
 * En B.6.a solo necesitamos los GETs para el Official Viewer:
 *  - getArtifact(id) → metadata del artifact (status, current_version_id, etc.)
 *  - getArtifactVersion(artifactId, versionId) → versión con `content` JSONB.
 *
 * Las mutations (POST /artifacts, PUT content, submit, decide) entran en
 * B.6.c con el editor real.
 */

import { apiFetch, apiFetchBinary, apiFetchForm } from './client'
import type { TemplateDefinition } from '../components/projects/official-viewer/section_types'
import type { SupportedLocale } from '../i18n/config'

// Note: 'external_canon' is the literal enum value from the API contract;
// kept verbatim. UI labels it as "Aportado por el cliente" / "Client-provided".
export type ArtifactStatus =
  | 'draft'
  | 'in_review'
  | 'approved'
  | 'superseded'
  | 'external_canon'

/** OT-0007 §4.1 — eje independiente de `ArtifactStatus`: cómo se
 *  produce el contenido, no en qué estado del workflow está. */
export type ArtifactSourceMode = 'template' | 'upload'

export interface Artifact {
  id: string
  tenant_id: string
  project_id: string
  phase_id: string | null
  template_key: string
  template_version: string
  name: string
  /** Code legible del artifact (ej. "art-CHR-001"). Lo expone el endpoint
   * LIST con JOIN; en GET single y POST llega como null hasta que el
   * service lo asigne (B.6.c.x cuando se trabaje el code generator). */
  code: string | null
  /** Sub-fase a la que pertenece (`'1.1'`, `'2.3'`, …). Snapshot del
   * lifecycle al materializar el artifact (B.6.c.1.5). */
  subphase_code: string | null
  status: ArtifactStatus
  current_version_id: string | null
  /** API contract field — frontend converts to `isOfficial` after this point. */
  is_canon: boolean
  /** OT-0007 §4.1 — plan de producción: redactado contra la plantilla
   *  (editor + secciones) o aportado por el cliente (zona de carga).
   *  Decide qué renderiza el Lifecycle drill-in y el viewer oficial. */
  source_mode: ArtifactSourceMode
  /** Metadata de la versión actual — populated por el endpoint LIST
   * via JOIN para evitar N+1. En POST/GET single estos campos vienen
   * en null (el caller pide /artifacts/{id}/versions/{vid} si necesita
   * el content). Disponibles desde B.6.c.1.5. */
  current_version_number: number | null
  current_version_hash: string | null
  current_version_approved_at: string | null
  /** OT-0008 §4.5 (D15) — "última edición" real del documento. Sale de
   *  la VERSIÓN, no del artefacto: `updated_at` del artefacto también se
   *  mueve al cambiar status o dueño, así que como "última edición"
   *  mentiría. Fecha y persona vienen de la misma fila para que no
   *  puedan contradecirse. */
  current_version_updated_at: string | null
  current_version_updated_by_user_id: string | null
  created_at: string
  updated_at: string
  created_by_user_id: string | null
  /** OT-0008 §4.4 — responsable ACTUAL del entregable. Una persona
   *  nombrada, no una inferencia del rol: con 0 o 2+ candidatos del rol
   *  el backend lo deja en `null` a propósito y el líder decide.
   *
   *  El nombre se resuelve contra la lista de miembros del proyecto, que
   *  la vista ya carga — el dueño siempre es miembro vigente porque la
   *  baja de un miembro fuerza reasignación o lo libera. */
  owner_user_id: string | null
  /** Doc 07 — Si hay una draft post-canon v(N+1) en progreso
   * (creada via POST /artifacts/{id}/new-version), el frontend muestra
   * badge "Borrador en progreso" + acción "Editar borrador" en
   * Lifecycle drill-in y banner en el viewer. Null cuando el artifact
   * solo tiene la canon vigente (sin draft post-canon abierta). */
  active_draft_version_id: string | null
  active_draft_version_number: number | null
  active_draft_change_reason: string | null
}

/**
 * Versión inmutable de un artifact. `content` es el JSONB con los
 * valores por section_id según el template del snapshot — ver Doc 06
 * §"Cómo la IA usa una plantilla". Cada key de `content` es un
 * `section_id` y el valor depende del `type` declarado en el template:
 *   - rich_text / plain_text → string (markdown / texto plano)
 *   - bullet_list / numbered_list → string[] o {text: string}[]
 *   - key_value_grid → {key: string, value: string}[]
 *   - structured → object con su schema
 *   - …
 */
/** Referencia al archivo cargado de una versión (OT-0007 §4.3). Un solo
 *  elemento por versión en esta OT — se deja como lista porque
 *  `blob_refs` ya es JSONB array en el backend y los block types de
 *  adjuntos (fuera de esta OT) la van a necesitar. */
export interface ArtifactBlobRef {
  blob_path: string
  filename: string
  mime_type: string
  size_bytes: number
  content_hash: string
  uploaded_by_user_id: string | null
  uploaded_at: string
}

export interface ArtifactVersion {
  id: string
  artifact_id: string
  version_number: number
  /** Mapa section_id → valor según `type` declarado en el template. */
  content: Record<string, unknown>
  content_language: string
  /** OT-0007 §4.3 — poblado solo cuando `Artifact.source_mode === 'upload'`. */
  blob_refs: ArtifactBlobRef[]
  produced_by: string
  hash: string | null
  change_reason: string | null
  /** Doc 21 Fase 5 — discriminador del intento de submit actual. Lo usa
   *  el ApprovalPanel para filtrar comments del cycle activo al derivar
   *  el estado del gate de canon (Fase 6). */
  review_cycle: number
  created_at: string
  approved_at: string | null
  superseded_at: string | null
  /** Doc 07 — timestamp ISO de cuándo el autor / admin / project lead
   *  descartó esta versión draft. Null para drafts activas y para
   *  versiones aprobadas (inmutables). El backend filtra estas
   *  versiones de `get_latest_canon_version` y `get_active_draft_version`. */
  discarded_at: string | null
  /** Doc 22 Fase 8.b — timestamp ISO de cuándo la task de canon
   *  indexing terminó (vectores en Qdrant + nodos en Neo4j). Null
   *  mientras la task corre o si nunca arrancó (ej. version pre-Fase
   *  8). El frontend muestra badge "indexing pending" cuando
   *  `approved_at !== null && indexed_at === null`. */
  indexed_at: string | null
  /** ADR-0002 Fase 10.f.2 — capturas de cross-refs consumidas al generar
   *  el draft. Cada item matchea `CrossArtifactCapture` del backend.
   *  El editor renderiza un banner cuando hay capturas con
   *  `was_canon_at_capture: false` (la IA generó usando un source en
   *  borrador, conviene revisar si el source cambia). */
  cross_artifact_captures?: CrossArtifactCapture[]
  /** ADR-0002 Fase 10.e — flag puesto por el drift detection task
   *  cuando el source de una cross-ref consumida cambia de canon. El
   *  frontend muestra badge "Drift detectado" en el listado del
   *  artifact + un CTA "Revisar sección impactada" en el editor. */
  cross_ref_drift_pending?: boolean
  /** OT-0064 — la versión admite cambios según la regla única del backend
   *  (`is_version_mutable`): no aprobada, no descartada y no congelada en
   *  revisión. Sólo expresa el estado; los permisos del usuario se evalúan
   *  aparte (`canEditArtifact`). El frontend no recalcula esta regla. */
  is_editable: boolean
}

/** Shape de cada item de `cross_artifact_captures`. ADR-0002 Fase 10.d. */
export interface CrossArtifactCapture {
  consumer_section_id: string
  source_template_key: string
  source_section_id: string
  source_artifact_id: string
  source_version_id: string
  /** SHA-256 hex del rendered de la sección consumida al momento de
   *  captura. Lo usa el backend para detectar drift; frontend solo lo
   *  pasa por. */
  source_content_hash: string
  /** True si el source era canon al momento de la captura; false si era
   *  draft (lo cual dispara el warning UX). */
  was_canon_at_capture: boolean
  /** ADR-0002 Fase 10.f UX — labels legibles del template y sección
   *  consumidos, resueltos por el backend del snapshot del proyecto en
   *  el locale activo. Frontend los prefiere sobre el ref técnico
   *  `template_key.section_id` al renderizar el banner del editor. */
  source_template_name?: string | null
  source_section_title?: string | null
}

export async function getArtifact(
  accessToken: string,
  artifactId: string,
): Promise<Artifact> {
  return apiFetch<Artifact>(`/api/v1/artifacts/${artifactId}`, { accessToken })
}

export interface ListProjectArtifactsFilters {
  /** Limit a un status específico. */
  artifact_status?: ArtifactStatus | undefined
  /** Limit a los artifacts de esa fase del proyecto (`ProjectPhase.id`). */
  phase_id?: string | undefined
}

/** Lista TODOS los artifacts del proyecto (drafts, in_review, approved,
 * superseded), con metadata de la versión actual joineada para evitar
 * N+1. Lo consume el Lifecycle drill-in. */
export async function listProjectArtifacts(
  accessToken: string,
  projectId: string,
  filters?: ListProjectArtifactsFilters,
): Promise<Artifact[]> {
  const params = new URLSearchParams()
  if (filters?.artifact_status)
    params.set('artifact_status', filters.artifact_status)
  if (filters?.phase_id) params.set('phase_id', filters.phase_id)
  const qs = params.toString()
  return apiFetch<Artifact[]>(
    `/api/v1/projects/${projectId}/artifacts${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

/**
 * Plantilla del artifact, PROYECTADA para render (OT-0013 §4.1) —
 * `GET /artifacts/{id}/template`. NO es el `definition` completo del
 * snapshot: excluye `ai_instructions`, `ai_global_instructions`,
 * `tone_audience`, `purpose`, `context_sections` y `validations` (el
 * prompt engineering del tenant). Alcanza a cualquier tier que pase
 * `require_artifact_view`, incluido `external` sobre lo que aprueba —
 * a diferencia de `fetchSnapshotTemplate` (project-scoped, bloqueado
 * para `external`).
 *
 * El shape es un subconjunto compatible de `TemplateDefinition`: trae
 * todo lo que `ArtifactPreview`/`SectionRenderer` consumen para
 * renderizar en solo lectura (incluido `repeatable` para expandir
 * grupos repetibles), nada de lo que solo usa `SectionEditor`.
 */
export async function getArtifactTemplate(
  accessToken: string,
  artifactId: string,
): Promise<TemplateDefinition> {
  return apiFetch<TemplateDefinition>(
    `/api/v1/artifacts/${artifactId}/template`,
    { accessToken },
  )
}

export async function getArtifactVersion(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<ArtifactVersion> {
  return apiFetch<ArtifactVersion>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}`,
    { accessToken },
  )
}

// ─── Mutations (B.6.c.1) ────────────────────────────────────────────────────

export interface CreateArtifactBody {
  /** Debe matchear un `template_key` del snapshot del proyecto. */
  template_key: string
  /** Nombre que escribió el usuario (opcional). Docs/19 §3.3.2: se
   * persiste aparte de `artifacts.name` (siempre el técnico, heredado
   * de la plantilla) y nunca se traduce. Si no se pasa, el backend usa
   * el nombre localizado de la plantilla. */
  custom_name?: string
  /** OT-0011 — sub-fase declarada por el lifecycle (viene de
   * `SnapshotTemplateSummary.subphase_id` del picker). Es la autoridad
   * sobre dónde cae el documento y sobre qué guard de repetibilidad
   * aplica al crear. */
  subphase_code?: string
}

/** Crea un artifact en estado `draft` con la version 1 vacía vinculada
 * como `current_version_id`. La versión arranca con `content={}`. */
export async function createArtifact(
  accessToken: string,
  projectId: string,
  body: CreateArtifactBody,
): Promise<Artifact> {
  return apiFetch<Artifact>(`/api/v1/projects/${projectId}/artifacts`, {
    accessToken,
    method: 'POST',
    body,
  })
}

/** Actualiza el `content` de una versión draft.
 *
 * Doc 07 — `versionId` opcional: para editar una draft v(N+1) post-canon
 * el caller debe pasarlo explícito (el artifact.current_version_id
 * sigue apuntando al canon v(N)). Si se omite, el backend usa
 * current_version_id (modo legacy: v1 inicial).
 */
export async function updateArtifactContent(
  accessToken: string,
  artifactId: string,
  content: Record<string, unknown>,
  versionId?: string | null,
): Promise<ArtifactVersion> {
  const qs = versionId
    ? `?version_id=${encodeURIComponent(versionId)}`
    : ''
  return apiFetch<ArtifactVersion>(
    `/api/v1/artifacts/${artifactId}/content${qs}`,
    {
      accessToken,
      method: 'PUT',
      body: { content },
    },
  )
}

// ─── Approval workflow (Doc 21 — Approval Workflow epic, Fase 2.a) ────────

/** Status posible de una approval (matches backend `ApprovalStatus`).
 *  `cancelled` aparece cuando un stage anterior fue rejected y se
 *  invalidaron todas las approvals pending del workflow. */
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

/** Approval row tal como la expone el backend (`ApprovalRead`). */
export interface Approval {
  id: string
  artifact_version_id: string
  approver_user_id: string
  approver_role: string
  status: ApprovalStatus
  decision_at: string | null
  comment: string | null
  created_at: string
  /** Nuevos campos multi-stage (Approval Workflow epic). Pueden venir
   *  null en approvals creadas antes de la migración 0028 — el viewer
   *  los trata como stage 0 mono-etapa. */
  stage_order?: number | null
  stage_name?: Partial<Record<SupportedLocale, string>> | string | null
  is_active?: boolean | null
  /** Identifica el ciclo de revisión al que pertenece esta approval
   *  (Issue #3, migración 0080). Cada rechazo+re-submit incrementa
   *  el cycle. El endpoint `?cycle=current` filtra al cycle activo. */
  review_cycle?: number | null
  /** OT-0013 §4.2 — nombre y rol del aprobador, ya resueltos en backend
   *  (locale por `Accept-Language`). Reemplaza el lookup que hacía
   *  `ApprovalPanel` contra `listProjectMembers`/`listTemplates(role)` —
   *  el primero bloqueaba al tier `external`, que no alcanza el
   *  directorio del proyecto. `null` solo si el backend no pudo
   *  resolver (no debería pasar en flujo normal). */
  approver_display_name: string | null
  approver_role_label: string | null
}

/** Resumen por ciclo del historial de aprobación (Issue #3).
 *  Lo devuelve `/approval-history`, ordenado por `review_cycle` DESC. */
export interface ApprovalCycleSummary {
  review_cycle: number
  status: 'rejected' | 'approved' | 'in_progress'
  decided_at: string | null
  decided_by_user_id: string | null
  decided_by_role: string | null
  decided_by_stage_order: number | null
  comment: string | null
  /** OT-0013 §4.2 — mismo criterio que `Approval.approver_display_name`
   *  / `approver_role_label`, con el prefijo `decided_by_*` porque
   *  acompañan a `decided_by_user_id`/`decided_by_role` de este mismo
   *  shape. `null` cuando el ciclo está `in_progress` (nadie decidió
   *  todavía) o el lookup no encontró nada. */
  decided_by_display_name: string | null
  decided_by_role_label: string | null
}

/** Aprobador legacy (un solo stage, un solo role). Backwards-compat
 *  con artifacts cuya subphase no declara workflow. El backend acepta
 *  este body desde el día 1; el modo `workflow_template_key` entra
 *  en Fase 2.e. */
export interface ApproverEntry {
  user_id: string
  role: string
}

/** Body de `POST /artifacts/{id}/submit`.
 *
 *  Dos modos:
 *    - **Legacy single-stage**: `{ approvers: [...] }`. Crea N approvals
 *      activas en paralelo en stage 0.
 *    - **Workflow auto-resolve**: `{}` (sin `approvers`). El backend
 *      localiza el subphase del artifact en el snapshot, resuelve el
 *      workflow declarado (override embebido > template > vacío) y
 *      crea las approvals multi-stage activando solo el primer stage.
 *      Si el subphase no declara workflow → fast-path self-approve.
 *
 *  El override del submitter (cambiar quién aprueba en cada stage)
 *  entra en una iteración futura cuando lo demande el wizard.
 */
export interface SubmitForReviewBody {
  approvers?: ApproverEntry[]
}

/** Pasa una versión del artifact de `draft` a `in_review` y crea
 *  las approvals pending del primer stage activo. El backend congela
 *  el content (snapshot) y persiste el workflow resuelto en
 *  `artifact_version_workflows`.
 *
 *  Doc 07 — `versionId` opcional: para submitir una draft v(N+1)
 *  post-canon el caller debe pasarlo explícito. Si se omite, se usa
 *  `current_version_id` del artifact (modo legacy: v1 inicial).
 */
export async function submitForReview(
  accessToken: string,
  artifactId: string,
  body: SubmitForReviewBody,
  versionId?: string | null,
): Promise<Artifact> {
  const qs = versionId
    ? `?version_id=${encodeURIComponent(versionId)}`
    : ''
  return apiFetch<Artifact>(`/api/v1/artifacts/${artifactId}/submit${qs}`, {
    accessToken,
    method: 'POST',
    body: body as unknown as Record<string, unknown>,
  })
}

// ─── Versionado post-canon (Doc 07) ──────────────────────────────────────────
export interface CreateNewVersionBody {
  /** Motivo de la nueva versión — obligatorio para trazabilidad. */
  change_reason: string
}

/** Abre una versión draft v(N+1) sobre el canon vigente.
 *  El canon NO se afecta. Una sola draft por artifact a la vez. */
export async function createNewVersion(
  accessToken: string,
  artifactId: string,
  body: CreateNewVersionBody,
): Promise<ArtifactVersion> {
  return apiFetch<ArtifactVersion>(
    `/api/v1/artifacts/${artifactId}/new-version`,
    {
      accessToken,
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    },
  )
}

/** Descarta una versión draft (marca discarded_at). El canon previo
 *  permanece como oficial vigente.
 *
 *  Permisos backend (devuelve 403 DISCARD_NOT_ALLOWED si falla):
 *    - autor de la versión, o
 *    - tenant admin, o
 *    - project lead (rol con is_project_lead=true en el catálogo). */
export async function discardDraftVersion(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/discard`,
    { accessToken, method: 'POST' },
  )
}

export interface DecideApprovalBody {
  approve: boolean
  comment?: string
}

/** Approver registra decisión (approve/reject) sobre la approval.
 *  Si todas las approvals del stage cierran (según `min_approvals`),
 *  se activa el siguiente stage. Si el stage final cierra, la versión
 *  se marca `is_canon=true`. */
export async function decideApproval(
  accessToken: string,
  approvalId: string,
  body: DecideApprovalBody,
): Promise<Approval> {
  return apiFetch<Approval>(`/api/v1/approvals/${approvalId}/decide`, {
    accessToken,
    method: 'POST',
    body: body as unknown as Record<string, unknown>,
  })
}

/** Lista approvals de una versión.
 *
 *  `cycle` (default `'current'`) — filtra al ciclo de revisión actual
 *  para que el ApprovalPanel no muestre filas históricas de ciclos
 *  rechazados previos. Pasa `'all'` para inspección completa (auditoría).
 *
 *  Backend: `/artifacts/{id}/versions/{vid}/approvals?cycle=current|all`.
 *  Ordenadas por `stage_order ASC, created_at ASC` (Issue #3). */
export async function listVersionApprovals(
  accessToken: string,
  artifactId: string,
  versionId: string,
  cycle: 'current' | 'all' = 'current',
): Promise<Approval[]> {
  return apiFetch<Approval[]>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/approvals?cycle=${cycle}`,
    { accessToken },
  )
}

/** Resumen del historial de ciclos de aprobación (Issue #3). UN row
 *  por cada cycle de la version, ordenado DESC. El frontend lo usa en
 *  el accordion "Historial" para mostrar líneas como
 *  "Ronda 3 — Rechazado por Ricardo (autor) — 21/5 4:42 p.m.". */
export async function getApprovalHistory(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<ApprovalCycleSummary[]> {
  return apiFetch<ApprovalCycleSummary[]>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/approval-history`,
    { accessToken },
  )
}

/** Approval pendiente con metadata mínima del artifact para el inbox
 *  del aprobador (Fase 2.d). Backend la materializa en Fase 2.e con
 *  un JOIN a artifacts + projects. */
export interface PendingApprovalForMe extends Approval {
  artifact_id: string
  artifact_name: string
  artifact_code: string | null
  project_id: string
  project_name: string
  /** Versión que se está aprobando — la pinta el inbox como "v3". */
  version_number: number
  submitted_at: string
}

/** Inbox del aprobador: todas las approvals pending+is_active=true
 *  que le tocan al usuario activo, en cualquier proyecto del tenant.
 *  Se ordena por `submitted_at` descendente (más reciente primero). */
export async function listMyPendingApprovals(
  accessToken: string,
): Promise<PendingApprovalForMe[]> {
  return apiFetch<PendingApprovalForMe[]>(`/api/v1/approvals/me/pending`, {
    accessToken,
  })
}

/** Artefacto sin responsable que el ciclo de vida declara para un rol
 *  funcional. Lo consume el atajo masivo de asignación (OT-0008 §4.4). */
export interface UnownedForRole {
  id: string
  name: string
  subphase_code: string | null
}

/**
 * Artefactos sin responsable declarados para `roleTemplateKey`.
 *
 * El cruce rol → artefacto vive en el lifecycle del snapshot y lo
 * resuelve el backend. NO se replica aquí: hacerlo en el cliente sería
 * reintroducir la lógica de selección que la OT-0008 eliminó.
 */
export async function listUnownedByRole(
  accessToken: string,
  projectId: string,
  roleTemplateKey: string,
): Promise<UnownedForRole[]> {
  const qs = new URLSearchParams({ role_template_key: roleTemplateKey })
  return apiFetch<UnownedForRole[]>(
    `/api/v1/projects/${projectId}/artifacts/unowned-by-role?${qs.toString()}`,
    { accessToken },
  )
}

export interface AssignByRoleResult {
  assigned_count: number
  artifact_ids: string[]
}

/**
 * Asigna en bloque los artefactos SIN responsable de un rol. Nunca
 * desplaza a un dueño existente — reasignar es una decisión individual.
 */
export async function assignOwnerByRole(
  accessToken: string,
  projectId: string,
  body: { role_template_key: string; owner_user_id: string },
): Promise<AssignByRoleResult> {
  return apiFetch<AssignByRoleResult>(
    `/api/v1/projects/${projectId}/artifacts/assign-owner-by-role`,
    { accessToken, method: 'POST', body },
  )
}

/** Asigna (o quita) el responsable de un artefacto — OT-0008 §4.4.
 *
 * Del **líder del proyecto o del admin**: el responsable no se
 * auto-asigna, si no cualquiera tomaría cualquier documento.
 *
 * `null` es un valor válido y significativo — deja el documento sin
 * responsable, que es el estado legítimo cuando hubo 0 o 2+ candidatos
 * del rol y el sistema, a propósito, no eligió. */
export async function updateArtifactOwner(
  accessToken: string,
  artifactId: string,
  ownerUserId: string | null,
  reason?: string,
): Promise<Artifact> {
  return apiFetch<Artifact>(`/api/v1/artifacts/${artifactId}/owner`, {
    accessToken,
    method: 'PATCH',
    body: { owner_user_id: ownerUserId, ...(reason ? { reason } : {}) },
  })
}

// ─── Documentos cargados (OT-0007) ─────────────────────────────────────────

/** Cambia el modo de producción del artefacto — del **líder del
 *  proyecto o del admin** (OT-0007 §4.5), mismo criterio que asignar
 *  responsable: no es del dueño del documento, un documento cargado
 *  pierde estructura de secciones, redacción asistida por IA y
 *  trazabilidad a nivel sección.
 *
 *  `versionId` opcional: para un v(N+1) post-canon el caller debe
 *  pasarlo explícito (`artifact.current_version_id` sigue apuntando al
 *  canon v(N), Doc 07 — mismo patrón que `updateArtifactContent`). Si
 *  se omite, el backend usa `current_version_id` (modo legacy: v1
 *  inicial).
 *
 *  Solo aplica mientras haya un draft abierto (409
 *  `ARTIFACT_SOURCE_MODE_LOCKED` si no). Cambiar de modo con
 *  contenido/archivo ya presente en el eje contrario lo descarta. */
export async function updateArtifactSourceMode(
  accessToken: string,
  artifactId: string,
  sourceMode: ArtifactSourceMode,
  versionId?: string | null,
): Promise<Artifact> {
  const qs = versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''
  return apiFetch<Artifact>(`/api/v1/artifacts/${artifactId}/source-mode${qs}`, {
    accessToken,
    method: 'PATCH',
    body: { source_mode: sourceMode },
  })
}

/** Sube (o remplaza) el archivo de una versión en modo carga. Un solo
 *  archivo por versión — el anterior se borra del storage al subir uno
 *  nuevo. Fija `produced_by='external_upload'` en el backend. */
export async function uploadArtifactVersionFile(
  accessToken: string,
  artifactId: string,
  versionId: string,
  file: File,
): Promise<ArtifactVersion> {
  const formData = new FormData()
  formData.append('file', file)
  return apiFetchForm<ArtifactVersion>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/file`,
    { accessToken, formData },
  )
}

/** Descarga el archivo cargado de una versión. Devuelve `{ blob,
 *  filename }` — el caller construye el download en el browser (mismo
 *  patrón que `downloadProjectSource`). Sin restricción de draft:
 *  funciona igual sobre un canon ya oficial. */
export async function downloadArtifactVersionFile(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  return apiFetchBinary(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/file`,
    { accessToken },
  )
}

/** Borra el archivo de una versión draft. Idempotente. */
export async function deleteArtifactVersionFile(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/file`,
    { accessToken, method: 'DELETE' },
  )
}

/**
 * Descarga la versión renderizada en PDF (OT-0027). El backend decide si
 * sale sellada como oficial o marcada como borrador según el estado real
 * de la versión — el frontend no elige variante.
 *
 * Responde 409 `PDF_EXPORT_NOT_AVAILABLE_FOR_UPLOAD` en documentos
 * aportados como archivo; para esos se usa
 * `downloadArtifactVersionFile`.
 */
export async function downloadArtifactVersionPdf(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  return apiFetchBinary(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/pdf`,
    { accessToken },
  )
}
